import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  OmsOrder,
  OrderItemFulfillment,
  FulfillmentStatus,
  OrderStatus,
  StorefrontSnapshot,
  OmsCustomerAddress,
  parseCustomerDataBlob,
} from "../../model/Oms";
import {
  PaymentAttempt,
  PaymentListResponse,
  PaymentStatus,
  isAttemptForOrder,
} from "../../model/Payment";
import api from "../../hooks/api";
import { API_URLS, getOrderComponentName } from "../../App";
import { orderShortRef } from "../../helpers/orderRef";
import { useLayoutContext } from "../../context/LayoutContext";

interface OmsOrderDetailProps {
  order: OmsOrder;
  onOrderUpdated: (order: OmsOrder) => void;
}

// ── Helpers ────────────────────────────────────────────────────────────────

function formatCents(cents: number, currency: string): string {
  return new Intl.NumberFormat("pt-PT", {
    style: "currency",
    currency: currency || "EUR",
  }).format(cents / 100);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("pt-PT");
}

const AddressBlock: React.FC<{ address?: OmsCustomerAddress }> = ({
  address,
}) => {
  const str = (v: unknown): string | undefined =>
    typeof v === "string" && v.trim() !== "" ? v.trim() : undefined;
  const city = str(address?.city);
  const postal = str(address?.postal_code);
  const country = str(address?.country);

  const lines = [str(address?.line1), str(address?.line2)];
  if (postal || city) lines.push([postal, city].filter(Boolean).join(" "));
  if (country) lines.push(country);

  const visible = lines.filter((l): l is string => l !== undefined);
  if (visible.length === 0) {
    return <span className="text-gray-400">—</span>;
  }
  return (
    <address className="not-italic text-sm text-gray-600 leading-relaxed">
      {visible.map((line, idx) => (
        <div key={idx}>{line}</div>
      ))}
    </address>
  );
};

// ── Status maps ────────────────────────────────────────────────────────────

const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  PENDING_OFFER: "Pendente Proposta",
  PAYMENT_PENDING: "Pagamento Pendente",
  PAID: "Pago",
  FULFILLMENT_INIT: "A Processar",
  PAID_PENDING_FULFILLMENT: "Pago — Fulfillment Pendente",
  COMPLETED: "Concluído",
  REFUNDED: "Reembolsado",
  FAILED: "Falhado",
  CANCELLED: "Cancelado",
  OFFER_SENT: "Proposta Enviada",
};

const ORDER_STATUS_COLORS: Record<OrderStatus, string> = {
  PENDING_OFFER: "bg-yellow-100 text-yellow-800",
  PAYMENT_PENDING: "bg-orange-100 text-orange-800",
  PAID: "bg-blue-100 text-blue-800",
  FULFILLMENT_INIT: "bg-indigo-100 text-indigo-800",
  PAID_PENDING_FULFILLMENT: "bg-purple-100 text-purple-800",
  COMPLETED: "bg-green-100 text-green-800",
  REFUNDED: "bg-gray-200 text-gray-700",
  FAILED: "bg-red-100 text-red-800",
  CANCELLED: "bg-red-50 text-red-700",
  OFFER_SENT: "bg-cyan-100 text-cyan-800",
};

const FULFILLMENT_STATUS_LABELS: Record<FulfillmentStatus, string> = {
  PENDING: "Pendente",
  CREATED: "Criado",
  ACCEPTED: "Aceite",
  CONFIRMED: "Confirmado",
  CANCELLED: "Cancelado",
  FAILED: "Falhado",
};

const FULFILLMENT_STATUS_COLORS: Record<FulfillmentStatus, string> = {
  PENDING: "bg-gray-100 text-gray-700",
  CREATED: "bg-blue-100 text-blue-700",
  ACCEPTED: "bg-indigo-100 text-indigo-700",
  CONFIRMED: "bg-green-100 text-green-700",
  CANCELLED: "bg-yellow-100 text-yellow-800",
  FAILED: "bg-red-100 text-red-800",
};

const PAYMENT_STATUS_LABELS: Record<PaymentStatus, string> = {
  pending: "Pendente",
  processing: "A processar",
  succeeded: "Pago",
  failed: "Falhado",
  cancelled: "Cancelado",
};

const PAYMENT_STATUS_COLORS: Record<PaymentStatus, string> = {
  pending: "bg-yellow-100 text-yellow-800",
  processing: "bg-blue-100 text-blue-800",
  succeeded: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-800",
  cancelled: "bg-gray-100 text-gray-700",
};

// ── Component snapshot helpers ───────────────────────────────────────────

/**
 * Parse the storefront_snapshot JSON field from an order item.
 * Returns null when absent or malformed.
 */
function parseStorefrontSnapshot(
  item: OrderItemFulfillment,
): StorefrontSnapshot | null {
  if (!item.storefront_snapshot) return null;
  try {
    const raw = JSON.parse(item.storefront_snapshot) as StorefrontSnapshot;
    // H-3: guard — if selected_components_snapshot is present but not an array
    // (e.g. legacy shape mismatch), drop it so we degrade to the ID fallback.
    if (
      raw.selected_components_snapshot !== undefined &&
      !Array.isArray(raw.selected_components_snapshot)
    ) {
      raw.selected_components_snapshot = undefined;
    }
    return raw;
  } catch {
    return null;
  }
}

/**
 * Renders the component breakdown for a single fulfillment item.
 * Uses the rich storefront_snapshot when available (has names + prices);
 * falls back to the raw selected_components IDs map.
 * Returns null when the item has no component data.
 */
const ItemComponentLines: React.FC<{
  item: OrderItemFulfillment;
  currency: string;
}> = ({ item, currency }) => {
  const snap = parseStorefrontSnapshot(item);

  if (snap?.selected_components_snapshot?.length) {
    // Rich snapshot — show type label, component name and price.
    return (
      <ul className="mt-1 space-y-0.5" aria-label="Componentes incluídos">
        {snap.selected_components_snapshot.map((entry, idx) => (
          <li
            key={`${entry.type_key}-${idx}`}
            className="flex items-center justify-between min-w-0 text-xs text-gray-500 pl-2 border-l-2 border-gray-200"
          >
            <span className="font-medium text-gray-600 mr-1 shrink-0">
              {entry.type_label || entry.type_key}:
            </span>
            <span className="flex-1 truncate min-w-0" title={entry.name}>
              {entry.name}
            </span>
            {typeof entry.unit_price_cents === "number" &&
              entry.unit_price_cents > 0 && (
                <span className="ml-2 text-gray-400 whitespace-nowrap shrink-0">
                  {formatCents(entry.unit_price_cents, currency)}
                </span>
              )}
          </li>
        ))}
      </ul>
    );
  }

  // Fallback: parse selected_components IDs (no names available)
  let rawComponents: Record<string, string> | null = null;
  if (snap?.selected_components) {
    rawComponents = snap.selected_components;
  } else if (item.selected_components) {
    try {
      rawComponents = JSON.parse(item.selected_components);
    } catch {
      rawComponents = null;
    }
  }

  if (!rawComponents || Object.keys(rawComponents).length === 0) {
    // Builder-quote checkout orders store no component data at checkout time.
    // Show a fallback badge so staff knows this is a custom build.
    if (item.compound_id === "generic_custom_build") {
      return (
        <p className="text-xs text-gray-400 italic mt-1 pl-2 border-l-2 border-gray-200">
          Orçamento personalizado — componentes disponíveis no detalhe do
          orçamento
        </p>
      );
    }
    return null;
  }

  return (
    <ul className="mt-1 space-y-0.5" aria-label="Componentes (IDs)">
      {Object.entries(rawComponents).map(([typeKey, componentId], idx) => (
        <li
          key={`${typeKey}-${idx}`}
          className="flex items-center min-w-0 text-xs text-gray-400 pl-2 border-l-2 border-gray-200"
        >
          {/* H-2: use getOrderComponentName to translate known type keys */}
          <span className="font-medium text-gray-500 mr-1 shrink-0">
            {getOrderComponentName(typeKey)}:
          </span>
          <span className="truncate min-w-0 font-mono" title={componentId}>
            {componentId}
          </span>
        </li>
      ))}
    </ul>
  );
};

// ── StatusBadge ────────────────────────────────────────────────────────────

const StatusBadge: React.FC<{ label: string; colorClass: string }> = ({
  label,
  colorClass,
}) => (
  <span
    className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${colorClass}`}
  >
    {label}
  </span>
);

// ── Main component ─────────────────────────────────────────────────────────

type Tab = "order" | "payment";

const OmsOrderDetail: React.FC<OmsOrderDetailProps> = ({
  order: initialOrder,
  onOrderUpdated,
}) => {
  const [order, setOrder] = useState<OmsOrder>(initialOrder);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>("order");
  const { setSuccess, setError, setModalScreen } = useLayoutContext();
  const navigate = useNavigate();

  // ── Refresh order on mount (garantir dados frescos da API) ────────────
  useEffect(() => {
    let cancelled = false;
    api
      .get<OmsOrder>(`${API_URLS.OMS_ADMIN_ORDERS}/${initialOrder.id}`)
      .then((res) => {
        if (!cancelled) setOrder(res.data);
      })
      .catch(() => {
        /* non-fatal — use initial prop as fallback */
      });
    return () => {
      cancelled = true;
    };
  }, [initialOrder.id]);

  // Payment tab state
  const [payments, setPayments] = useState<PaymentAttempt[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentsFetched, setPaymentsFetched] = useState(false);

  // ── Fetch payments when tab opens ─────────────────────────────────────

  const fetchPayments = async () => {
    if (paymentsFetched) return;
    setPaymentsLoading(true);
    try {
      // A API de listagem é tenant-wide (ignora session_id) — o escopo à
      // ordem é feito no cliente em isAttemptForOrder.
      const params = new URLSearchParams();
      params.set("page_size", "50");
      const res = await api.get<PaymentListResponse>(
        `${API_URLS.PAYMENT_ATTEMPTS}?${params}`,
      );
      const scoped = (res.data.data || []).filter((p) =>
        isAttemptForOrder(p, {
          orderId: order.id,
          session: order.checkout_session_id || undefined,
          attemptId: order.payment_attempt_id || undefined,
        }),
      );
      setPayments(scoped);
      setPaymentsFetched(true);
    } catch (e: any) {
      setError(e?.response?.data?.error || "Erro ao carregar pagamentos.");
    } finally {
      setPaymentsLoading(false);
    }
  };

  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab);
    if (tab === "payment") fetchPayments();
  };

  // ── Actions ───────────────────────────────────────────────────────────

  const handleCancelItem = async (item: OrderItemFulfillment) => {
    setActionLoading(`cancel-${item.id}`);
    try {
      await api.delete(
        `${API_URLS.OMS_ADMIN_ORDERS}/${order.id}/items/${item.id}`,
      );
      const res = await api.get<OmsOrder>(
        `${API_URLS.OMS_ADMIN_ORDERS}/${order.id}`,
      );
      setOrder(res.data);
      onOrderUpdated(res.data);
      setSuccess(`Item "${item.display_name}" cancelado com sucesso.`);
    } catch (e: any) {
      setError(e?.response?.data?.error || "Erro ao cancelar item.");
    } finally {
      setActionLoading(null);
    }
  };

  const handleRetryItem = async (item: OrderItemFulfillment) => {
    setActionLoading(`retry-${item.id}`);
    try {
      await api.post(
        `${API_URLS.OMS_ADMIN_ORDERS}/${order.id}/items/${item.id}/retry`,
      );
      const res = await api.get<OmsOrder>(
        `${API_URLS.OMS_ADMIN_ORDERS}/${order.id}`,
      );
      setOrder(res.data);
      onOrderUpdated(res.data);
      setSuccess(`Item "${item.display_name}" reprocessado com sucesso.`);
    } catch (e: any) {
      setError(e?.response?.data?.error || "Erro ao reprocessar item.");
    } finally {
      setActionLoading(null);
    }
  };

  const handleConfirmPayment = async () => {
    setActionLoading("confirm-payment");
    try {
      await api.patch(
        `${API_URLS.OMS_ADMIN_ORDERS}/${order.id}/confirm-payment`,
      );
      const res = await api.get<OmsOrder>(
        `${API_URLS.OMS_ADMIN_ORDERS}/${order.id}`,
      );
      setOrder(res.data);
      onOrderUpdated(res.data);
      setSuccess("Pagamento confirmado manualmente.");
    } catch (e: any) {
      setError(e?.response?.data?.error || "Erro ao confirmar pagamento.");
    } finally {
      setActionLoading(null);
    }
  };

  // ── Derived ───────────────────────────────────────────────────────────

  const orderStatusColor =
    ORDER_STATUS_COLORS[order.status] || "bg-gray-100 text-gray-700";
  const orderStatusLabel = ORDER_STATUS_LABELS[order.status] || order.status;
  const aggStatusColor =
    FULFILLMENT_STATUS_COLORS[order.aggregate_status] ||
    "bg-gray-100 text-gray-700";
  const aggStatusLabel =
    FULFILLMENT_STATUS_LABELS[order.aggregate_status] || order.aggregate_status;

  const customerBlob = useMemo(
    () => parseCustomerDataBlob(order.customer_data),
    [order.customer_data],
  );
  const shippingAddress = customerBlob.shipping_address;

  // Referência Niposom/IAPC: em builder-quote orders a offer pré-aprovada vive
  // ao nível da encomenda; em checkout orders o fan-out cria uma offer por item
  // niposom (usamos a 1ª). provider_type vazio/nulo = fan-out niposom legado
  // (dual-write do OMS não preenche o campo em ordens antigas) — ainda é ref
  // de offer válida. Refs de providers webhook não linkam /offers.
  const niposomOfferRef =
    order.niposom_offer_id ??
    order.items?.find((it) => {
      const isLegacyNiposom =
        it.provider_type === "niposom" ||
        it.provider_type == null ||
        it.provider_type === "";
      return isLegacyNiposom && it.provider_reference;
    })?.provider_reference;

  // ── IAPC order deep-link ──────────────────────────────────────────────

  const handleOpenIapcOrder = async () => {
    if (actionLoading) return;
    // modalScreen é app-level (Layout), acima das rotas: fechar SEMPRE antes de
    // navegar, senão o modal opaco sobrevive à troca de rota e o onClose ainda
    // faz pushState de volta para a listagem (URL desincronizado do conteúdo).
    const go = (path: string) => {
      setModalScreen(null);
      navigate(path, { replace: true });
    };
    if (!niposomOfferRef) {
      go("/orders");
      return;
    }
    setActionLoading("open-iap-order");
    try {
      // Resolve a order IAPC cuja offer (fan-out Niposom) é esta ref e abre o
      // detalhe /orders/:id. Sem ordem (ex.: antes do accept) a API responde
      // 404 — mantemos a página e informamos o staff.
      const res = await api.get<{ id?: number | string }>(
        `${API_URLS.ORDERS}/by-offer/${niposomOfferRef}`,
      );
      const orderId = res.data?.id;
      if (orderId) {
        go(`/orders/${orderId}`);
      } else {
        setError("Ordem IAPC não encontrada para esta proposta.");
      }
    } catch (e: any) {
      if (e?.response?.status === 404) {
        setError(
          "Ainda não existe ordem IAPC — é criada automaticamente quando a proposta é aceite, após o pagamento ser confirmado.",
        );
      } else {
        setError(e?.response?.data?.error || "Erro ao procurar ordem IAPC.");
      }
    } finally {
      setActionLoading(null);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full">
      {/* ── Inner tabs ── */}
      <div className="flex border-b border-gray-200 px-4 pt-2 shrink-0">
        <button
          className={`px-5 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "order"
              ? "border-blue-600 text-blue-700"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
          onClick={() => handleTabChange("order")}
        >
          Encomenda
        </button>
        <button
          className={`px-5 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "payment"
              ? "border-blue-600 text-blue-700"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
          onClick={() => handleTabChange("payment")}
        >
          Pagamento
        </button>
      </div>

      {/* ── Scrollable content ── */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* ══ ORDER TAB ══ */}
        <div className={activeTab === "order" ? "" : "hidden"}>
          <div className="flex flex-col gap-6">
            {/* Header card */}
            <div className="bg-gray-50 rounded-lg p-4 flex flex-col gap-2">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-mono text-xs text-gray-400">
                  {order.id}
                </span>
                <StatusBadge
                  label={orderStatusLabel}
                  colorClass={orderStatusColor}
                />
                <StatusBadge
                  label={`Fulfillment: ${aggStatusLabel}`}
                  colorClass={aggStatusColor}
                />
              </div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm mt-2">
                <div>
                  <span className="text-gray-500">Ref.:</span>{" "}
                  <span className="font-mono font-medium">
                    {orderShortRef(order.id)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Método de pagamento:</span>{" "}
                  <span>{order.payment_method || "—"}</span>
                </div>
                <div>
                  <span className="text-gray-500">Método de envio:</span>{" "}
                  <span>{order.delivery_method?.label || "—"}</span>
                </div>
                <div>
                  <span className="text-gray-500">Cliente:</span>{" "}
                  <span className="font-medium">{order.customer_name}</span>
                </div>
                <div>
                  <span className="text-gray-500">Email:</span>{" "}
                  <span className="font-medium">{order.customer_email}</span>
                </div>
                <div>
                  <span className="text-gray-500">Subtotal:</span>{" "}
                  <span>
                    {formatCents(order.subtotal_cents, order.currency)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Envio:</span>{" "}
                  <span>
                    {formatCents(order.shipping_cents, order.currency)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Total:</span>{" "}
                  <span className="font-bold text-gray-800">
                    {formatCents(order.total_cents, order.currency)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Criado:</span>{" "}
                  <span>{formatDate(order.created_at)}</span>
                </div>
                <div>
                  <span className="text-gray-500">Atualizado:</span>{" "}
                  <span>{formatDate(order.updated_at)}</span>
                </div>
                {order.notes && (
                  <div className="col-span-2">
                    <span className="text-gray-500">Notas:</span>{" "}
                    <span>{order.notes}</span>
                  </div>
                )}
                {order.failure_reason && (
                  <div className="col-span-2">
                    <span className="text-red-500">Motivo de falha:</span>{" "}
                    <span className="text-red-700">{order.failure_reason}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Shipping address — expanded from customer_data */}
            {shippingAddress?.line1 && (
              <div className="bg-gray-50 rounded-lg p-3">
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">
                  Morada de Envio
                </h4>
                <AddressBlock address={shippingAddress} />
              </div>
            )}

            {/* Pickup point — enriched from OMS selected_poi */}
            {order.selected_poi && (
              <div className="bg-gray-50 rounded-lg p-3">
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">
                  Ponto de Levantamento
                </h4>
                <div className="text-sm font-medium text-gray-700 mb-1">
                  {order.selected_poi.label}
                </div>
                <AddressBlock
                  address={{
                    line1: order.selected_poi.line1,
                    line2: order.selected_poi.line2,
                    city: order.selected_poi.city,
                    postal_code: order.selected_poi.postal_code,
                    country: order.selected_poi.country,
                  }}
                />
              </div>
            )}

            {/* Atalhos IAPC */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-gray-500">IAPC:</span>
              {niposomOfferRef && (
                <a
                  className="button is-small is-outlined"
                  href={`/offers/${niposomOfferRef}`}
                  title={`Proposta IAPC ${niposomOfferRef}`}
                >
                  Ver proposta
                </a>
              )}
              <button
                className="button is-small is-outlined"
                onClick={handleOpenIapcOrder}
                disabled={!!actionLoading}
                title="Abrir a ordem correspondente no IAPC"
              >
                {actionLoading === "open-iap-order"
                  ? "A abrir…"
                  : "Ver ordem IAPC"}
              </button>
            </div>

            {/* Confirm Payment action */}
            {order.status === "PAYMENT_PENDING" && (
              <div>
                <button
                  className="button is-success is-small"
                  disabled={actionLoading === "confirm-payment"}
                  onClick={handleConfirmPayment}
                >
                  {actionLoading === "confirm-payment"
                    ? "A confirmar..."
                    : "Confirmar Pagamento Manualmente"}
                </button>
              </div>
            )}

            {/* Items table */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">
                Itens da Encomenda ({order.items?.length ?? 0})
              </h3>
              {!order.items || order.items.length === 0 ? (
                <p className="text-sm text-gray-400 italic">Sem itens.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-gray-100 text-left text-xs text-gray-600 uppercase">
                        <th className="px-3 py-2">Artigo</th>
                        <th className="px-3 py-2">Preço Unit.</th>
                        <th className="px-3 py-2">Estado</th>
                        <th className="px-3 py-2">Provider</th>
                        <th className="px-3 py-2">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {order.items.map((item) => {
                        const fsColor =
                          FULFILLMENT_STATUS_COLORS[item.fulfillment_status] ||
                          "bg-gray-100 text-gray-700";
                        const fsLabel =
                          FULFILLMENT_STATUS_LABELS[item.fulfillment_status] ||
                          item.fulfillment_status;
                        const canCancel = ![
                          "CANCELLED",
                          "CONFIRMED",
                          "FAILED",
                        ].includes(item.fulfillment_status);
                        const canRetry = item.fulfillment_status === "FAILED";
                        return (
                          <tr
                            key={item.id}
                            className="border-t border-gray-100 hover:bg-gray-50"
                          >
                            <td className="px-3 py-2 font-medium">
                              <div>{item.display_name}</div>
                              <ItemComponentLines
                                item={item}
                                currency={order.currency}
                              />
                            </td>
                            <td className="px-3 py-2">
                              {formatCents(
                                item.unit_price_cents,
                                order.currency,
                              )}
                            </td>
                            <td className="px-3 py-2">
                              <StatusBadge
                                label={fsLabel}
                                colorClass={fsColor}
                              />
                            </td>
                            <td className="px-3 py-2 text-xs text-gray-500">
                              {item.provider_type || "—"}
                              {item.provider_reference
                                ? ` · ${item.provider_reference}`
                                : ""}
                            </td>
                            <td className="px-3 py-2 flex gap-2">
                              {canCancel && (
                                <button
                                  className="button is-danger is-small is-outlined"
                                  disabled={
                                    actionLoading === `cancel-${item.id}`
                                  }
                                  onClick={() => handleCancelItem(item)}
                                >
                                  {actionLoading === `cancel-${item.id}`
                                    ? "..."
                                    : "Cancelar"}
                                </button>
                              )}
                              {canRetry && (
                                <button
                                  className="button is-warning is-small is-outlined"
                                  disabled={
                                    actionLoading === `retry-${item.id}`
                                  }
                                  onClick={() => handleRetryItem(item)}
                                >
                                  {actionLoading === `retry-${item.id}`
                                    ? "..."
                                    : "Retry"}
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ══ PAYMENT TAB ══ */}
        <div className={activeTab === "payment" ? "" : "hidden"}>
          {paymentsLoading ? (
            <p className="text-gray-400 py-8 text-center">
              A carregar pagamentos...
            </p>
          ) : payments.length === 0 && paymentsFetched ? (
            <p className="text-gray-400 py-8 text-center italic">
              Sem registos de pagamento para esta encomenda.
            </p>
          ) : (
            <div className="flex flex-col gap-4">
              {payments.map((attempt) => {
                const statusLabel =
                  PAYMENT_STATUS_LABELS[attempt.status] || attempt.status;
                const statusColor =
                  PAYMENT_STATUS_COLORS[attempt.status] ||
                  "bg-gray-100 text-gray-700";

                let parsedMeta: object | null = null;
                try {
                  if (attempt.metadata)
                    parsedMeta = JSON.parse(attempt.metadata);
                } catch {
                  // ignore
                }

                return (
                  <div
                    key={attempt.id}
                    className="bg-gray-50 rounded-lg p-4 border border-gray-100"
                  >
                    <div className="flex items-center gap-3 mb-3 flex-wrap">
                      <span className="font-mono text-xs text-gray-400">
                        {attempt.id}
                      </span>
                      <StatusBadge
                        label={statusLabel}
                        colorClass={statusColor}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                      <div>
                        <span className="text-gray-500">Montante:</span>{" "}
                        <span className="font-bold text-gray-800">
                          {formatCents(attempt.amount, attempt.currency)}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Moeda:</span>{" "}
                        <span>{attempt.currency}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Ref. Provider:</span>{" "}
                        <span className="font-mono text-xs">
                          {attempt.provider_ref || "—"}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Idempotency Key:</span>{" "}
                        <span className="font-mono text-xs break-all">
                          {attempt.idempotency_key}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Criado:</span>{" "}
                        <span>{formatDate(attempt.created_at)}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Atualizado:</span>{" "}
                        <span>{formatDate(attempt.updated_at)}</span>
                      </div>
                      {attempt.failure_reason && (
                        <div className="col-span-2">
                          <span className="text-red-500">Motivo de falha:</span>{" "}
                          <span className="text-red-700">
                            {attempt.failure_reason}
                          </span>
                        </div>
                      )}
                    </div>
                    {parsedMeta && (
                      <div className="mt-3">
                        <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">
                          Metadata
                        </h4>
                        <pre className="bg-white rounded border border-gray-100 p-3 text-xs overflow-x-auto text-gray-700">
                          {JSON.stringify(parsedMeta, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OmsOrderDetail;
